<script setup lang="ts">
import type { Column } from './table-types';

/**
 * Columns are declared rather than written as markup, so every table in the
 * product aligns, wraps and scrolls the same way.
 *
 * Wide content scrolls inside its own container: a long record value or a JSON
 * audit payload must not make the page scroll sideways.
 */
defineProps<{ columns: readonly Column[]; caption?: string | undefined }>();
</script>

<template>
	<div class="border-border bg-surface overflow-x-auto rounded-xl border shadow-xs">
		<table class="w-full border-collapse text-sm">
			<caption v-if="caption" class="sr-only">
				{{
					caption
				}}
			</caption>
			<thead>
				<tr>
					<th
						v-for="column in columns"
						:key="column.key"
						scope="col"
						class="border-border text-muted bg-bg/60 border-b px-4 py-2.5 text-left text-xs font-medium tracking-wide uppercase"
					>
						{{ column.label }}
					</th>
				</tr>
			</thead>
			<tbody>
				<slot />
			</tbody>
		</table>
	</div>
</template>

<style scoped>
/*
 * The last row drops its rule: inside a card the container's own edge is the
 * bottom line, and a second one under it reads as a stray divider.
 *
 * A rule rather than a utility, because the state belongs to the row and the
 * class would have to go on the cell. Asking every caller to mark its rows
 * would mean a forgotten class shows up as a double border nobody traces back.
 */
:deep(tbody tr:last-child td) {
	border-bottom-width: 0;
}

/* Only where a pointer can hover, so a touch tap does not leave a row lit. */
@media (hover: hover) {
	:deep(tbody tr:hover td) {
		background: color-mix(in oklab, var(--c-border) 22%, transparent);
	}
}
</style>
